import React, {
  useState,
  useEffect,
  useRef,
  useReducer,
  useCallback,
  useMemo,
} from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import PageContainer from "@/components/layouts/PageContainer";
import { Heading } from "@/components/heading";
import { Button } from "@/components/button";
import { ArrowLeftIcon, CheckCircleIcon, TrashIcon, LockClosedIcon } from "@heroicons/react/20/solid";
import {
  BoldIcon,
  ItalicIcon,
  UnderlineIcon,
  Bars3BottomLeftIcon,
  Bars3Icon,
  Bars3BottomRightIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
} from "@heroicons/react/24/outline";
import { useQueryGetExamPaper } from "@/apis/exam_papers/getExamPaper";
import { useUpdateExamPaper } from "@/apis/exam_papers/updateExamPaper";
import { useQueryGetQuestion } from "@/apis/questions/getQuestion";
import { useCreateQuestion } from "@/apis/questions/createQuestion";
import { useQueryGetQuestionsEditable } from "@/apis/exam_papers/getQuestionsEditable";
import { useQueryGetExamConfig } from "@/apis/exam_configs/getExamConfig";
import { Tabs, Spin, message, Switch, Input } from "antd";
import type { TextAreaRef } from "antd/es/input/TextArea";
import { Select } from "@/components/select";
import type { IQuestion } from "@/apis/exam_papers/types";
import { Text } from "@/components/text";
import { useUpdateQuestion } from "@/apis/questions/updateQuestion";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import katex from "katex";
import "katex/dist/katex.min.css";
import { apiUploadImage } from "@/apis/upload/apiUploadImage";
import { useQueryGetCategories } from "@/apis/categories/getCategories";
import QuestionNumberSelector from "@/pages/exam_papers/components/QuestionNumberSelector";
import { convertToFraction } from "@/utils/math";
import { createPasteHandler, preserveHtmlSpaces } from "@/utils/quillClipboard";
import {
  NO_TOOLBAR_QUILL_MODULES,
  applyQuillIndent,
  createNoToolbarQuillModules,
} from "@/utils/quillEditorModules";
import { getPaperTypeLabel } from "@/constants/exam";

import {
  useQueryGetPassage,
  useUpdatePassage,
  useCreatePassage,
} from "@/apis/passages";

// Quill과 KaTeX 설정
declare global {
  interface Window {
    katex: any;
    Quill: any;
  }
}

// KaTeX를 window에 연결하여 Quill에서 접근 가능하게 함
if (typeof window !== "undefined") {
  window.katex = katex;

  // Quill 모듈 등록
  if (typeof window.Quill !== "undefined") {
    try {
      const Formula = window.Quill.import("formats/formula");
      if (!Formula) {
        const Embed = window.Quill.import("blots/embed");

        class FormulaBlot extends Embed {
          static blotName = "formula";
          static tagName = "span";
          static className = "ql-formula";

          static create(value: string) {
            const node = super.create(value);
            if (typeof value === "string") {
              try {
                window.katex.render(value, node, {
                  throwOnError: false,
                  displayMode: false,
                });
                node.setAttribute("data-value", value);
              } catch (e) {
                node.textContent = value;
              }
            }
            return node;
          }

          static value(node: Element) {
            return node.getAttribute("data-value");
          }
        }

        window.Quill.register(FormulaBlot, true);
      }
    } catch (e) {
      console.warn("Quill formula module registration failed:", e);
    }
  }
}

// const { TabPane } = Tabs; // deprecated, using items instead

// Editor State 관리
interface EditorState {
  // 문제 관련
  editedPassage: string;
  editedQuestion: string;
  currentChoices: any[];
  correctChoiceId: string;
  answerType: string;
  shortAnswer: string;
  selectedCategoryId: string;
  
  // 상태 관련
  isDirty: boolean;
  saving: boolean;
  lastSaved: Date | null;
  
  // Shared passage 관련
  linkedPassageId: string | null;
  editedSharedPassageContent: string;
  isCreatingNewPassage: boolean;
  newPassageContent: string;
}

type EditorAction =
  | { type: 'LOAD_QUESTION'; payload: { question: any; linkedPassage?: any; choiceCount: number } }
  | { type: 'FIELD_UPDATE'; payload: { field: keyof EditorState; value: any } }
  | { type: 'UPDATE_CHOICE'; payload: { index: number; text: string } }
  | { type: 'SET_ANSWER_TYPE'; payload: { answerType: string; choiceCount: number } }
  | { type: 'SAVE_SUCCESS' };

const initialEditorState: EditorState = {
  editedPassage: "",
  editedQuestion: "",
  currentChoices: [],
  correctChoiceId: "",
  answerType: "multiple_choice",
  shortAnswer: "",
  selectedCategoryId: "",
  isDirty: false,
  saving: false,
  lastSaved: null,
  linkedPassageId: null,
  editedSharedPassageContent: "",
  isCreatingNewPassage: false,
  newPassageContent: "",
};

const CHOICE_IDS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

const adjustChoicesToCount = (choices: any[], choiceCount: number): any[] => {
  if (choices.length === choiceCount) return choices;
  if (choices.length > choiceCount) return choices.slice(0, choiceCount);
  const result = [...choices];
  while (result.length < choiceCount) {
    result.push({ id: CHOICE_IDS[result.length] || String.fromCharCode(65 + result.length), text: '' });
  }
  return result;
};

const editorReducer = (state: EditorState, action: EditorAction): EditorState => {
  switch (action.type) {
    case 'LOAD_QUESTION': {
      const { question, linkedPassage, choiceCount } = action.payload;
      const loadedAnswerType = question.answer_type || "multiple_choice";
      const rawChoices = question.choices || [];
      const adjustedChoices = loadedAnswerType === "multiple_choice"
        ? adjustChoicesToCount(rawChoices, choiceCount)
        : rawChoices;
      return {
        ...state,
        editedPassage: preserveHtmlSpaces(question.passage || ""),
        editedQuestion: question.question || "",
        currentChoices: adjustedChoices,
        correctChoiceId: question.correct_choice_id || "",
        answerType: loadedAnswerType,
        shortAnswer: loadedAnswerType === "short_answer" ? (question.correct_text_answer || "") : "",
        selectedCategoryId: question.category_id || "",
        linkedPassageId: question.passage_id || null,
        editedSharedPassageContent: preserveHtmlSpaces(linkedPassage?.content || ""),
        isDirty: false,
      };
    }
    
    case 'FIELD_UPDATE':
      const { field, value } = action.payload;
      // 특정 필드는 isDirty를 false로 유지 (저장 관련)
      const shouldMarkDirty = !['saving', 'lastSaved'].includes(field);
      return {
        ...state,
        [field]: value,
        ...(shouldMarkDirty && { isDirty: true }),
      };
    
    case 'UPDATE_CHOICE':
      const newChoices = [...state.currentChoices];
      newChoices[action.payload.index].text = action.payload.text;
      return {
        ...state,
        currentChoices: newChoices,
        isDirty: true,
      };
    
    case 'SET_ANSWER_TYPE': {
      const { answerType: newAnswerType, choiceCount: atChoiceCount } = action.payload;
      const newChoices = newAnswerType === "multiple_choice"
        ? adjustChoicesToCount(state.currentChoices, atChoiceCount)
        : state.currentChoices;
      return {
        ...state,
        answerType: newAnswerType,
        currentChoices: newChoices,
        isDirty: true,
      };
    }

    case 'SAVE_SUCCESS':
      return {
        ...state,
        isDirty: false,
        saving: false,
        lastSaved: new Date(),
      };

    default:
      return state;
  }
};

// 에디터 스타일 설정 (컴포넌트 외부에서 정의하여 재생성 방지)
const EDITOR_STYLE = {
  passage: {
    fontFamily: '"Times New Roman", Times, serif',
    fontSize: "18px",
  },
  question: {
    fontFamily: '"Times New Roman", Times, serif',
    fontSize: "18px",
  },
  choice: {
    fontFamily: '"Times New Roman", Times, serif',
    fontSize: "18px",
  },
} as const;

// 커스텀 툴바 컴포넌트
interface CustomToolbarProps {
  quillRef: React.RefObject<ReactQuill | null>;
  editorId: string;
  showEmphasis?: boolean;
}

const CustomToolbar: React.FC<CustomToolbarProps> = ({
  quillRef,
  editorId,
  showEmphasis = false,
}) => {
  const handleFormat = (format: string, value?: any) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const selection = quill.getSelection();
    if (!selection) return;

    if (format === "align") {
      quill.format("align", value === "left" ? false : value);
    } else {
      const currentValue = quill.getFormat(selection)[format];
      quill.format(format, !currentValue);
    }

    quill.focus();
  };

  const handleIndent = (increase: boolean) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    applyQuillIndent(quill, increase);
    quill.focus();
  };

  return (
    <div className="flex items-center gap-1 p-2 border-b border-gray-200 bg-gray-50 sticky top-0 z-10">
      {/* Text Formatting */}
      <div className="flex items-center gap-1 mr-3">
        <button
          type="button"
          onClick={() => handleFormat("bold")}
          className="p-2 rounded hover:bg-gray-200 text-gray-600 cursor-pointer"
          title="Bold (Ctrl+B)"
        >
          <BoldIcon className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => handleFormat("italic")}
          className="p-2 rounded hover:bg-gray-200 text-gray-600 cursor-pointer"
          title="Italic (Ctrl+I)"
        >
          <ItalicIcon className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => handleFormat("underline")}
          className="p-2 rounded hover:bg-gray-200 text-gray-600 cursor-pointer"
          title="Underline (Ctrl+U)"
        >
          <UnderlineIcon className="w-4 h-4" />
        </button>
      </div>

      {/* Divider */}
      <div className="w-px h-6 bg-gray-300 mr-3"></div>

      {/* Instructor Emphasis (passage editor only) */}
      {showEmphasis && (
        <>
          <div className="flex items-center gap-1 mr-3">
            <button
              type="button"
              onClick={() => handleFormat("instructorEmphasis")}
              className="p-2 rounded hover:bg-gray-200 text-gray-600 cursor-pointer"
              title="Emphasis"
            >
              <div
                className="w-4 h-4 rounded-full border border-gray-400"
                style={{ backgroundColor: "var(--instructor-emphasis-bg)" }}
              />
            </button>
          </div>

          {/* Divider */}
          <div className="w-px h-6 bg-gray-300 mr-3"></div>
        </>
      )}

      {/* Indentation */}
      <div className="flex items-center gap-1 mr-3">
        <button
          type="button"
          onClick={() => handleIndent(true)}
          className="p-2 rounded hover:bg-gray-200 text-gray-600 cursor-pointer"
          title="Increase Indent (Ctrl+])"
        >
          <ChevronRightIcon className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => handleIndent(false)}
          className="p-2 rounded hover:bg-gray-200 text-gray-600 cursor-pointer"
          title="Decrease Indent (Ctrl+[)"
        >
          <ChevronLeftIcon className="w-4 h-4" />
        </button>
      </div>

      {/* Divider */}
      <div className="w-px h-6 bg-gray-300 mr-3"></div>

      {/* Alignment */}
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => handleFormat("align", "left")}
          className="p-2 rounded hover:bg-gray-200 text-gray-600 cursor-pointer"
          title="Align Left"
        >
          <Bars3BottomLeftIcon className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => handleFormat("align", "center")}
          className="p-2 rounded hover:bg-gray-200 text-gray-600 cursor-pointer"
          title="Align Center"
        >
          <Bars3Icon className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => handleFormat("align", "right")}
          className="p-2 rounded hover:bg-gray-200 text-gray-600 cursor-pointer"
          title="Align Right"
        >
          <Bars3BottomRightIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const ExamPaperEditQuestionsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get current indices from URL params with validation
  const currentModuleIndex = Math.max(0, parseInt(searchParams.get('module') || '0'));
  const currentQuestionId = searchParams.get('questionId') || '';

  // Helper functions to update URL params
  const setCurrentModuleIndex = (moduleIndex: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('module', moduleIndex.toString());
    // Reset to first question of the module when changing module
    if (examPaper?.sections?.[moduleIndex]?.questions?.[0]?.id) {
      newParams.set('questionId', examPaper.sections[moduleIndex].questions[0].id);
    }
    setSearchParams(newParams);
  };

  const setCurrentQuestionId = (questionId: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('questionId', questionId);
    setSearchParams(newParams);
  };
  // useReducer로 상태 관리
  const [editorState, dispatch] = useReducer(editorReducer, initialEditorState);
  
  // 편의성을 위한 상태 추출
  const {
    editedPassage,
    editedQuestion,
    currentChoices,
    correctChoiceId,
    saving,
    lastSaved,
    isDirty,
    answerType,
    shortAnswer,
    selectedCategoryId,
    linkedPassageId,
    editedSharedPassageContent,
    isCreatingNewPassage,
    newPassageContent,
  } = editorState;

  // Quill 에디터 참조
  const passageQuillRef = useRef<ReactQuill>(null);
  const questionQuillRef = useRef<ReactQuill>(null);
  const choiceQuillRefs = useRef<(ReactQuill | null)[]>([]);
  const answerTypeSelectRef = useRef<HTMLSelectElement>(null);
  const categorySelectRef = useRef<HTMLSelectElement>(null);
  const shortAnswerRef = useRef<TextAreaRef>(null);

  const focusPassageEditor = useCallback((): boolean => {
    const passageEditor = passageQuillRef.current?.getEditor();
    if (!passageEditor) return false;

    passageEditor.focus();
    return true;
  }, []);

  const focusQuestionEditor = useCallback((): boolean => {
    const questionEditor = questionQuillRef.current?.getEditor();
    if (!questionEditor) return false;

    questionEditor.focus();
    return true;
  }, []);

  const focusAnswerTypeSelect = useCallback((): boolean => {
    if (!answerTypeSelectRef.current) return false;

    answerTypeSelectRef.current.focus();
    return true;
  }, []);

  const focusCategorySelect = useCallback((): boolean => {
    if (!categorySelectRef.current) return false;

    categorySelectRef.current.focus();
    return true;
  }, []);

  const focusShortAnswer = useCallback((): boolean => {
    if (!shortAnswerRef.current) return false;

    shortAnswerRef.current.focus();
    return true;
  }, []);

  const focusChoiceEditor = useCallback((targetIndex: number): boolean => {
    const targetEditor = choiceQuillRefs.current[targetIndex]?.getEditor();
    if (!targetEditor) return false;

    targetEditor.focus();
    return true;
  }, []);

  const focusFirstTabField = useCallback(
    (): boolean => focusPassageEditor() || focusQuestionEditor(),
    [focusPassageEditor, focusQuestionEditor]
  );

  const focusPreviousFromQuestion = useCallback(
    (): boolean => focusPassageEditor() || focusCategorySelect(),
    [focusPassageEditor, focusCategorySelect]
  );

  const focusFirstAnswerField = useCallback((): boolean => {
    if (answerType === "multiple_choice") {
      return focusChoiceEditor(0) || focusCategorySelect();
    }

    return focusShortAnswer() || focusCategorySelect();
  }, [answerType, focusChoiceEditor, focusCategorySelect, focusShortAnswer]);

  const focusLastAnswerField = useCallback((): boolean => {
    if (answerType === "multiple_choice") {
      return (
        focusChoiceEditor(currentChoices.length - 1) || focusAnswerTypeSelect()
      );
    }

    return focusShortAnswer() || focusAnswerTypeSelect();
  }, [
    answerType,
    currentChoices.length,
    focusAnswerTypeSelect,
    focusChoiceEditor,
    focusShortAnswer,
  ]);

  const handleTabNavigation = useCallback(
    (
      event: React.KeyboardEvent,
      focusNext: () => boolean,
      focusPrevious: () => boolean
    ) => {
      if (event.key !== "Tab") return;

      const moved = event.shiftKey ? focusPrevious() : focusNext();
      if (!moved) return;

      event.preventDefault();
      event.stopPropagation();
    },
    []
  );

  const passageQuillModules = useMemo(
    () =>
      createNoToolbarQuillModules({
        onTab: (direction) =>
          direction === 1 ? focusQuestionEditor() : focusCategorySelect(),
      }),
    [focusCategorySelect, focusQuestionEditor]
  );

  const questionQuillModules = useMemo(
    () =>
      createNoToolbarQuillModules({
        onTab: (direction) =>
          direction === 1 ? focusAnswerTypeSelect() : focusPreviousFromQuestion(),
      }),
    [focusAnswerTypeSelect, focusPreviousFromQuestion]
  );

  const choiceQuillModules = useMemo(
    () =>
      Array.from({ length: currentChoices.length }, (_, index) =>
        createNoToolbarQuillModules({
          onTab: (direction) => {
            if (direction === -1) {
              return index > 0
                ? focusChoiceEditor(index - 1)
                : focusAnswerTypeSelect();
            }

            return index < currentChoices.length - 1
              ? focusChoiceEditor(index + 1)
              : focusCategorySelect();
          },
        })
      ),
    [
      currentChoices.length,
      focusAnswerTypeSelect,
      focusCategorySelect,
      focusChoiceEditor,
    ]
  );

  // Quill 모듈 등록 확인 및 설정
  useEffect(() => {
    const setupQuillFormula = () => {
      if (typeof window !== "undefined" && window.Quill) {
        try {
          window.katex = katex;
          const Embed = window.Quill.import("blots/embed");

          class FormulaBlot extends Embed {
            static blotName = "formula";
            static tagName = "span";
            static className = "ql-formula";

            static create(value: string) {
              const node = super.create(value);
              if (typeof value === "string") {
                try {
                  window.katex.render(value, node, {
                    throwOnError: false,
                    displayMode: false,
                  });
                  node.setAttribute("data-value", value);
                } catch (e) {
                  node.textContent = value;
                }
              }
              return node;
            }

            static value(node: Element) {
              return node.getAttribute("data-value");
            }
          }

          try {
            window.Quill.register(FormulaBlot, true);
            console.log("Formula blot registered successfully");
          } catch (e) {
            console.warn(
              "Formula blot already registered or registration failed:",
              e
            );
          }
        } catch (error) {
          console.error("Quill formula setup failed:", error);
        }
      }
    };

    const timer = setTimeout(setupQuillFormula, 100);
    return () => clearTimeout(timer);
  }, []);

  // Query exam paper data (for structure and navigation)
  const {
    data: examPaper,
    isLoading: isLoadingExamPaper,
    error: examPaperError,
    refetch: refetchExamPaper,
  } = useQueryGetExamPaper(id || "");

  // Query individual question data
  const {
    data: currentQuestion,
    isLoading: isLoadingQuestion,
    error: questionError,
    refetch: refetchQuestion,
  } = useQueryGetQuestion(currentQuestionId, {
    enabled: !!currentQuestionId,
  });

  // Query exam config data
  const { data: examConfig } = useQueryGetExamConfig(
    examPaper?.exam_type || "",
    {
      enabled: !!examPaper?.exam_type,
    }
  );

  // choice_count: ExamPaper 오버라이딩 → ExamConfig 기본값 → 4
  const configChoiceCount = examConfig?.subjects?.find(s => s.subject === examPaper?.subject)?.choice_count || 4;
  const choiceCount = examPaper?.choice_count || configChoiceCount;

  // Query categories data
  const { data: categoriesData } = useQueryGetCategories({
    exam_type: examPaper?.exam_type || "",
    subject: examPaper?.subject || "",
  }, {
    enabled: !!examPaper?.exam_type && !!examPaper?.subject,
  });

  // 문제 업데이트 뮤테이션
  const updateQuestionMutation = useUpdateQuestion();
  const updateExamPaperMutation = useUpdateExamPaper();
  const createQuestionMutation = useCreateQuestion();

  // 구조 변경(추가/삭제/순서변경) 잠금 상태 조회
  const { data: questionsEditableData } = useQueryGetQuestionsEditable(
    id || "",
    { enabled: !!id }
  );
  const isStructureLocked = questionsEditableData?.editable === false;
  const lockedProgressCount = questionsEditableData?.progress_count ?? 0;

  // 낙관적 재정렬: 서버 순서와 다를 수 있는 임시 순서 (드래그 직후)
  const [optimisticQuestions, setOptimisticQuestions] = useState<IQuestion[] | null>(null);

  // 현재 섹션의 문제 목록 (낙관적 업데이트 반영)
  const currentModuleQuestions: IQuestion[] = React.useMemo(() => {
    if (optimisticQuestions) return optimisticQuestions;
    if (!examPaper || !examPaper.sections) return [];
    const section = examPaper.sections[currentModuleIndex];
    return section?.questions || [];
  }, [examPaper, currentModuleIndex, optimisticQuestions]);

  // 현재 섹션의 문제 ID들 (네비게이션용)
  const currentModuleQuestionIds = React.useMemo(
    () => currentModuleQuestions.map((q) => q.id),
    [currentModuleQuestions]
  );

  // Validate and adjust URL params when data loads
  useEffect(() => {
    if (!examPaper || !examPaper.sections) return;

    const maxModuleIndex = examPaper.sections.length - 1;
    const currentModuleQuestions = examPaper.sections[currentModuleIndex]?.questions || [];
    
    let needsUpdate = false;
    const newParams = new URLSearchParams(searchParams);

    // Adjust module index if out of bounds
    if (currentModuleIndex > maxModuleIndex) {
      const validModuleIndex = Math.max(0, maxModuleIndex);
      newParams.set('module', validModuleIndex.toString());
      const firstQuestionId = examPaper.sections[validModuleIndex]?.questions?.[0]?.id || '';
      newParams.set('questionId', firstQuestionId);
      needsUpdate = true;
    }
    // If questionId is not provided or invalid, set to first question
    else if (!currentQuestionId || !currentModuleQuestions.find(q => q.id === currentQuestionId)) {
      const firstQuestionId = currentModuleQuestions[0]?.id || '';
      if (firstQuestionId) {
        newParams.set('questionId', firstQuestionId);
        needsUpdate = true;
      }
    }

    if (needsUpdate) {
      setSearchParams(newParams, { replace: true });
    }
  }, [examPaper, currentModuleIndex, currentQuestionId, searchParams, setSearchParams]);

  // Get current question index for display purposes
  const currentQuestionIndex = currentModuleQuestionIds.indexOf(currentQuestionId);



  // 데이터 변경 감지 로직은 useReducer로 자동 처리됨 (isDirty가 액션에 포함됨)

  // 뒤로 가기
  const handleBack = () => {
    if (isDirty) {
      // 자동 저장 후 이동
      handleSave(true).then(() => {
        window.history.back();
      }).catch(() => {
        // 저장 실패 시 메시지 표시하고 이동하지 않음
        message.error("저장에 실패했습니다. 다시 시도해주세요.");
        console.error("Failed to save before going back");
      });
    } else {
      window.history.back();
    }
  };

  // 모듈 변경
  const handleModuleChange = (newModuleIndex: number) => {
    // 같은 모듈을 클릭한 경우 아무것도 하지 않음
    if (newModuleIndex === currentModuleIndex) {
      return;
    }

    // 새로고침으로 페이지 이동 (첫 번째 문제로)
    const firstQuestionId = examPaper?.sections?.[newModuleIndex]?.questions?.[0]?.id || '';
    const newUrl = `/exam_papers/${id}/edit_questions?module=${newModuleIndex}&questionId=${firstQuestionId}`;
    
    if (isDirty) {
      // 자동 저장 후 새로고침
      handleSave(true).then(() => {
        window.location.href = newUrl;
      }).catch(() => {
        // 저장 실패 시 메시지 표시하고 이동하지 않음
        message.error("저장에 실패했습니다. 다시 시도해주세요.");
        console.error("Failed to save before module change");
      });
    } else {
      // 변경사항 없으면 바로 새로고침
      window.location.href = newUrl;
    }
  };

  // 문제 변경
  const handleQuestionChange = (questionId: string) => {
    // 같은 문제를 클릭한 경우 아무것도 하지 않음
    if (questionId === currentQuestionId) {
      return;
    }

    // 새로고침으로 페이지 이동
    const newUrl = `/exam_papers/${id}/edit_questions?module=${currentModuleIndex}&questionId=${questionId}`;
    
    if (isDirty) {
      // 자동 저장 후 새로고침
      handleSave(true).then(() => {
        window.location.href = newUrl;
      }).catch(() => {
        // 저장 실패 시 메시지 표시하고 이동하지 않음
        message.error("저장에 실패했습니다. 다시 시도해주세요.");
        console.error("Failed to save before question change");
      });
    } else {
      // 변경사항 없으면 바로 새로고침
      window.location.href = newUrl;
    }
  };

    // 저장 처리
  const handleSave = async (showMessage = false) => {
    if (!currentQuestion) return Promise.resolve();
    if (!isDirty) return Promise.resolve();
    
    // 저장 시점의 문제 정보를 캡처하여 race condition 방지 (깊은 복사 사용)
    const questionToSave = currentQuestion;
    const passageToSave = preserveHtmlSpaces(editedPassage);
    const questionContentToSave = editedQuestion;
    const categoryIdToSave = selectedCategoryId;
    const answerTypeToSave = answerType;
    const choicesToSave = currentChoices.map(choice => ({ ...choice })); // 깊은 복사
    const correctChoiceToSave = correctChoiceId;
    const shortAnswerToSave = shortAnswer;
    
    dispatch({ type: 'FIELD_UPDATE', payload: { field: 'saving', value: true } });
    try {
      // Question 저장
      await updateQuestionMutation.mutateAsync({
        id: questionToSave.id,
        data: {
          passage: passageToSave,
          question: questionContentToSave,
          category_id: categoryIdToSave,
          answer_type: answerTypeToSave,
          ...(answerTypeToSave === "multiple_choice"
            ? {
                choices: choicesToSave,
                correct_choice_id: correctChoiceToSave,
                correct_text_answer: undefined,
              }
            : {
                choices: undefined,
                correct_choice_id: undefined,
                correct_text_answer: shortAnswerToSave,
              }),
        },
      });

      // Shared passage 저장 (변경된 경우에만)
      if (
        linkedPassageId &&
        linkedPassage &&
        editedSharedPassageContent !== linkedPassage.content
      ) {
        await updateSharedPassageMutation.mutateAsync({
          id: linkedPassageId,
          data: { content: preserveHtmlSpaces(editedSharedPassageContent) },
        });
      }

      dispatch({ type: 'SAVE_SUCCESS' });
      if (showMessage) {
        message.success("Question saved successfully");
      }
      refetchExamPaper();
      return Promise.resolve();
    } catch (error) {
      if (showMessage) {
        message.error("Failed to save question");
      }
      dispatch({ type: 'FIELD_UPDATE', payload: { field: 'saving', value: false } });
      return Promise.reject(error);
    }
  };



  // examPaper 재조회 완료 후 낙관적 상태 해제
  useEffect(() => {
    if (!optimisticQuestions || !examPaper?.sections) return;
    const serverQuestions = examPaper.sections[currentModuleIndex]?.questions || [];
    const serverIds = serverQuestions.map((q) => q.id).join(",");
    const optimisticIds = optimisticQuestions.map((q) => q.id).join(",");
    if (serverIds === optimisticIds) {
      setOptimisticQuestions(null);
    }
  }, [examPaper, currentModuleIndex, optimisticQuestions]);

  // 섹션의 question_ids만 패치하는 공통 헬퍼
  const patchSectionQuestionIds = async (
    moduleIdx: number,
    nextIds: string[]
  ) => {
    if (!examPaper?.id || !examPaper.sections) return;
    const updatedSections = examPaper.sections.map((section, i) => ({
      duration: section.duration,
      question_ids:
        i === moduleIdx ? nextIds : section.question_ids,
    }));
    await updateExamPaperMutation.mutateAsync({
      id: examPaper.id,
      data: { sections: updatedSections },
    });
    await refetchExamPaper();
  };

  // 순서 변경
  const handleReorderQuestions = async (nextIds: string[]) => {
    if (isStructureLocked) return;
    if (!examPaper?.sections) return;
    const section = examPaper.sections[currentModuleIndex];
    const serverQuestions = section?.questions || [];
    // 낙관적 업데이트
    const nextQuestions = nextIds
      .map((qid) => serverQuestions.find((q) => q.id === qid))
      .filter((q): q is IQuestion => !!q);
    setOptimisticQuestions(nextQuestions);
    try {
      await patchSectionQuestionIds(currentModuleIndex, nextIds);
    } catch (error) {
      console.error("Failed to reorder questions:", error);
      message.error("Failed to reorder questions");
      setOptimisticQuestions(null);
    }
  };

  // 문제 추가
  const handleAddQuestion = async () => {
    if (isStructureLocked) return;
    if (!examPaper?.id) return;
    try {
      const emptyChoices = Array.from({ length: choiceCount }, (_, i) => ({
        id: CHOICE_IDS[i] || String.fromCharCode(65 + i),
        text: "",
      }));
      const response = await createQuestionMutation.mutateAsync({
        exam_id: examPaper.id,
        answer_type: "multiple_choice",
        question: "<p></p>",
        choices: emptyChoices,
      });
      const created = response?.data;
      if (!created?.id) {
        message.error("Failed to create a new question");
        return;
      }
      const nextIds = [...currentModuleQuestionIds, created.id];
      await patchSectionQuestionIds(currentModuleIndex, nextIds);
      // 새 문제로 이동
      const newParams = new URLSearchParams(searchParams);
      newParams.set("module", currentModuleIndex.toString());
      newParams.set("questionId", created.id);
      setSearchParams(newParams);
    } catch (error) {
      console.error("Failed to add question:", error);
      message.error("Failed to add a new question");
    }
  };

  // 문제 삭제 (question_ids에서만 제거)
  const handleRemoveQuestion = async () => {
    if (isStructureLocked) return;
    if (!currentQuestionId) return;
    const confirmed = window.confirm(
      "Remove this question from the exam?\n\nThe question document will be kept, but it will no longer appear in this exam paper."
    );
    if (!confirmed) return;

    const prevIndex = currentModuleQuestionIds.indexOf(currentQuestionId);
    const nextIds = currentModuleQuestionIds.filter(
      (qid) => qid !== currentQuestionId
    );
    try {
      await patchSectionQuestionIds(currentModuleIndex, nextIds);
      // 삭제 후 이웃 문제로 이동
      const fallbackId =
        nextIds[prevIndex] ?? nextIds[prevIndex - 1] ?? nextIds[0] ?? "";
      const newParams = new URLSearchParams(searchParams);
      newParams.set("module", currentModuleIndex.toString());
      if (fallbackId) {
        newParams.set("questionId", fallbackId);
      } else {
        newParams.delete("questionId");
      }
      setSearchParams(newParams);
    } catch (error) {
      console.error("Failed to remove question:", error);
      message.error("Failed to remove the question");
    }
  };

  const isMutatingSection =
    updateExamPaperMutation.isPending || createQuestionMutation.isPending;

  // 선택지 편집기 (텍스트 변경 핸들러)
  const handleChoiceTextChange = (index: number, value: string) => {
    dispatch({ type: 'UPDATE_CHOICE', payload: { index, text: value } });
  };

  // 마지막 저장 시간 표시
  const getLastSavedText = () => {
    if (!lastSaved) return "";
    return `Last saved: ${lastSaved.toLocaleTimeString()}`;
  };

  // 이미지 차단 로직 - 문제 변경시마다 새 에디터들에 다시 연결
  useEffect(() => {
    const setupImageBlocking = () => {
      const quillRefs = [
        { ref: passageQuillRef, name: "passage" },
        { ref: questionQuillRef, name: "question" },
        ...choiceQuillRefs.current.map((ref, index) => ({
          ref: { current: ref },
          name: `choice-${index}`,
        })),
      ];

      const cleanupFunctions: (() => void)[] = [];

      quillRefs.forEach(({ ref, name }) => {
        if (ref.current) {
          const quill = ref.current.getEditor();

          const pasteHandler = createPasteHandler(
            quill,
            name,
            async (file: File) => {
              try {
                const response = await apiUploadImage(file);
                return response.url;
              } catch (error) {
                console.error("Image upload failed:", error);
                message.error("Failed to upload image");
                throw error;
              }
            }
          );

          quill.root.addEventListener("paste", pasteHandler, true);
          cleanupFunctions.push(() => {
            quill.root.removeEventListener("paste", pasteHandler, true);
          });
        }
      });

      return () => {
        cleanupFunctions.forEach((cleanup) => cleanup());
      };
    };

    // 문제 변경 후 새 에디터들이 생성될 때까지 대기
    const timer = setTimeout(setupImageBlocking, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [currentQuestion?.id]); // 🔧 currentQuestion?.id 의존성 추가 - 문제 변경시마다 재실행

  // 이미지 붙여넣기 핸들러 - 더 이상 사용되지 않음 (차단됨)
  // const handlePasteImage = async (quill: any, event: ClipboardEvent) => {
  //   // 이미지는 이제 paste 이벤트에서 차단되므로 이 함수는 호출되지 않음
  // };

  // 현재 subject의 passage_type을 확인하는 함수
  const getCurrentSubjectPassageType = () => {
    if (!examConfig || !examPaper) return "individual"; // 기본값

    const currentSubject = examConfig.subjects.find(
      (subject) => subject.subject === examPaper.subject
    );

    return currentSubject?.passage_type || "individual"; // 기본값
  };

  // passage가 필요한지 확인
  const isPassageRequired = getCurrentSubjectPassageType() !== "none";
  const isSharedPassage = getCurrentSubjectPassageType() === "shared";

  // Shared passage 관련 상태는 이제 useReducer에서 관리됨

  // 연결된 shared passage 조회
  const { data: linkedPassage } = useQueryGetPassage(linkedPassageId || "", {
    enabled: !!linkedPassageId,
  });

  // linkedPassage가 로드되면 editedSharedPassageContent 초기화
  useEffect(() => {
    if (linkedPassage) {
      dispatch({ type: 'FIELD_UPDATE', payload: { field: 'editedSharedPassageContent', value: preserveHtmlSpaces(linkedPassage.content || "") } });
    }
  }, [linkedPassage]);

  // 문제 변경될 때마다 에디터 상태 업데이트
  useEffect(() => {
    if (currentQuestion) {
      dispatch({
        type: 'LOAD_QUESTION',
        payload: { question: currentQuestion, linkedPassage, choiceCount }
      });
    }
  }, [currentQuestion?.id, linkedPassage?.content, choiceCount]);

  // Shared passage 수정 뮤테이션
  const updateSharedPassageMutation = useUpdatePassage();

  // Shared passage 생성 뮤테이션
  const createPassageMutation = useCreatePassage();

  // 새 passage 생성 시작
  const handleStartCreatingNewPassage = () => {
    dispatch({ type: 'FIELD_UPDATE', payload: { field: 'isCreatingNewPassage', value: true } });
    dispatch({ type: 'FIELD_UPDATE', payload: { field: 'newPassageContent', value: "" } });
  };

  // 새 passage 저장
  const handleSaveNewPassage = async () => {
    if (!newPassageContent.trim()) {
      return;
    }

    try {
      const result = await createPassageMutation.mutateAsync({
        exam_paper_id: examPaper?.id || "",
        content: preserveHtmlSpaces(newPassageContent),
      });

      if (result?.id) {
        dispatch({ type: 'FIELD_UPDATE', payload: { field: 'linkedPassageId', value: result.id } });
        dispatch({ type: 'FIELD_UPDATE', payload: { field: 'isCreatingNewPassage', value: false } });
        dispatch({ type: 'FIELD_UPDATE', payload: { field: 'newPassageContent', value: "" } });

        // Question을 업데이트하여 passage_id 연결
        if (currentQuestion) {
          await updateQuestionMutation.mutateAsync({
            id: currentQuestion.id,
            data: {
              passage_id: result.id,
              passage: "", // individual passage는 비움
            },
          });
          refetchExamPaper();
        }
      }
    } catch (error) {
      console.error("Failed to create passage:", error);
    }
  };

  // 새 passage 생성 취소
  const handleCancelCreatingNewPassage = () => {
    dispatch({ type: 'FIELD_UPDATE', payload: { field: 'isCreatingNewPassage', value: false } });
    dispatch({ type: 'FIELD_UPDATE', payload: { field: 'newPassageContent', value: "" } });
  };

  // 이전 문제의 passage에 링크
  const handleLinkToPreviousPassage = async () => {
    const currentModuleQuestions = examPaper?.sections?.[currentModuleIndex]?.questions || [];
    const prevQuestion = currentModuleQuestions[currentQuestionIndex - 1];
    if (prevQuestion?.passage_id && currentQuestion) {
      dispatch({ type: 'FIELD_UPDATE', payload: { field: 'linkedPassageId', value: prevQuestion.passage_id } });
      await updateQuestionMutation.mutateAsync({
        id: currentQuestion.id,
        data: {
          passage_id: prevQuestion.passage_id,
          passage: "", // individual passage는 비움
        },
      });
      await refetchQuestion();
      await refetchExamPaper();
    }
  };

  // 다음 문제의 passage에 링크
  const handleLinkToNextPassage = async () => {
    const currentModuleQuestions = examPaper?.sections?.[currentModuleIndex]?.questions || [];
    const nextQuestion = currentModuleQuestions[currentQuestionIndex + 1];
    if (nextQuestion?.passage_id && currentQuestion) {
      dispatch({ type: 'FIELD_UPDATE', payload: { field: 'linkedPassageId', value: nextQuestion.passage_id } });
      await updateQuestionMutation.mutateAsync({
        id: currentQuestion.id,
        data: {
          passage_id: nextQuestion.passage_id,
          passage: "", // individual passage는 비움
        },
      });
      await refetchQuestion();
      await refetchExamPaper();
    }
  };

  // Shared passage 연결 해제
  const handleUnlinkPassage = async () => {
    dispatch({ type: 'FIELD_UPDATE', payload: { field: 'linkedPassageId', value: null } });
    if (currentQuestion) {
      await updateQuestionMutation.mutateAsync({
        id: currentQuestion.id,
        data: {
          passage_id: null,
          passage: "", // individual passage로 되돌림
        },
      });
      refetchExamPaper();
    }
  };

  // Shared passage 수정 핸들러
  const handleSharedPassageChange = (content: string) => {
    dispatch({ type: 'FIELD_UPDATE', payload: { field: 'editedSharedPassageContent', value: content } });
  };

  // 로딩 상태
  if (isLoadingExamPaper || isLoadingQuestion) {
    return (
      <PageContainer>
        <div className="h-screen flex items-center justify-center">
          <Spin size="large">
          </Spin>
        </div>
      </PageContainer>
    );
  }

  // 에러 상태
  if (examPaperError || questionError || !examPaper) {
    return (
      <PageContainer>
        <div className="p-8 text-center text-red-600">
          <Text>Failed to load exam paper or question information. Please try again.</Text>
          <Button onClick={handleBack} className="mt-4">
            Back to Detail
          </Button>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer wide>
      <div className="max-w-screen-2xl mx-auto px-4">
        <div className="p-6">
          {/* 헤더 */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-3">
              <Button onClick={handleBack} plain className="rounded-full p-2">
                <ArrowLeftIcon className="h-5 w-5" />
              </Button>
              <Heading level={3}>Edit Questions: {examPaper.title}</Heading>
            </div>
            <div className="flex items-center gap-4">
              {/* 수동 저장 버튼 */}
              <Button
                onClick={() => handleSave(true)}
                disabled={saving || !isDirty}
                className="min-w-20"
              >
                {saving ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Save
                  </div>
                ) : (
                  "Save"
                )}
              </Button>
            </div>
          </div>

          {/* 편집 잠금 배너 */}
          {isStructureLocked && (
            <div className="mb-6 flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <LockClosedIcon className="h-4 w-4 flex-shrink-0 text-amber-600" />
              <span>
                응시 이력 {lockedProgressCount}건 — 문제 구조 변경 잠김 (내용 수정은 가능)
              </span>
            </div>
          )}

          {/* 모듈 선택 탭 */}
          <div className="mb-6">
            <Tabs
              activeKey={currentModuleIndex.toString()}
              onChange={(key) => handleModuleChange(parseInt(key))}
              type="card"
              className="mb-4"
              items={examPaper.sections?.map((section: any, index: number) => ({
                key: index.toString(),
                label: `Section ${index + 1}`,
              }))}
            />

            {/* 현재 섹션 정보 */}
            <div className="bg-blue-50 p-3 rounded-md mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <Text className="font-medium">
                    Section {currentModuleIndex + 1}
                  </Text>
                  <div className="text-sm text-gray-600 mt-1">
                    <span>
                      {examPaper.sections?.[currentModuleIndex]?.duration || 0}{" "}
                      minutes
                    </span>
                    <span className="mx-2">•</span>
                    <span>{currentModuleQuestionIds.length} questions</span>
                  </div>
                </div>
                <Button
                  color="white"
                  onClick={async () => {
                    if (isDirty) {
                      await handleSave();
                    }
                    window.location.href = `/exam_papers/${id}/preview/${currentModuleIndex + 1}`;
                  }}
                >
                  Preview All
                </Button>
              </div>
            </div>

            {/* 문제 번호 선택 */}
            <QuestionNumberSelector
              questions={currentModuleQuestions}
              currentQuestionIndex={currentModuleQuestionIds.indexOf(currentQuestionId)}
              onQuestionChange={(questionNumber) => {
                const questionId = currentModuleQuestionIds[questionNumber - 1];
                if (questionId) {
                  handleQuestionChange(questionId);
                }
              }}
              onReorder={isStructureLocked ? undefined : handleReorderQuestions}
              onAddQuestion={isStructureLocked ? undefined : handleAddQuestion}
              isUpdating={isMutatingSection || isStructureLocked}
            />
          </div>

          {/* 메인 에디터 */}
          {currentQuestion ? (
            <>
              <div className="flex justify-end items-center gap-2 mb-4">
                {!isStructureLocked && (
                  <Button
                    color="red"
                    onClick={handleRemoveQuestion}
                    disabled={isMutatingSection}
                    title="Remove from exam"
                  >
                    <TrashIcon className="w-4 h-4" />
                    Remove from exam
                  </Button>
                )}
                <Button
                  color="white"
                  onClick={async () => {
                    if (isDirty) {
                      await handleSave();
                    }
                    const questionNumber = currentModuleQuestionIds.indexOf(currentQuestionId) + 1;
                    window.location.href = `/exam_papers/${id}/preview/${
                      currentModuleIndex + 1
                    }?question=${questionNumber}`;
                  }}
                >
                  Preview
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-full overflow-hidden">
                {/* 좌측: 지문 영역 */}
                <div className="bg-gray-50 p-4 rounded-lg h-full flex flex-col">
                  <div className="mb-3 flex justify-between items-center">
                    <Text className="font-medium">Passage</Text>
                  </div>
                  <div className="quill-container w-full max-w-full flex flex-col overflow-auto min-h-0 flex-1 border border-gray-300 bg-white rounded">
                    {isPassageRequired ? (
                      isSharedPassage ? (
                        // Shared passage UI
                        linkedPassageId && linkedPassage ? (
                          <>
                            <div className="flex items-center justify-between p-2 bg-blue-50 border-b">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-blue-700">
                                  Shared Passage
                                </span>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  color="red"
                                  onClick={handleUnlinkPassage}
                                >
                                  Unlink
                                </Button>
                              </div>
                            </div>
                            <CustomToolbar
                              quillRef={passageQuillRef}
                              editorId="passage"
                              showEmphasis
                            />
                            <ReactQuill
                              key={`${currentQuestion?.id || 'default'}-shared-passage`}
                              theme="snow"
                              value={editedSharedPassageContent}
                              onChange={handleSharedPassageChange}
                              modules={passageQuillModules}
                              style={EDITOR_STYLE.passage}
                              className="font-times max-w-full flex-1"
                              ref={passageQuillRef}
                            />
                          </>
                        ) : isCreatingNewPassage ? (
                          // Creating new passage UI
                          <>
                            <div className="flex items-center justify-between p-2 bg-green-50 border-b">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-green-700">
                                  Creating New Shared Passage
                                </span>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  plain
                                  onClick={handleCancelCreatingNewPassage}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  onClick={handleSaveNewPassage}
                                  disabled={
                                    !newPassageContent.trim() ||
                                    createPassageMutation.isPending
                                  }
                                >
                                  {createPassageMutation.isPending
                                    ? "Saving..."
                                    : "Save"}
                                </Button>
                              </div>
                            </div>
                            <CustomToolbar
                              quillRef={passageQuillRef}
                              editorId="passage"
                              showEmphasis
                            />
                            <ReactQuill
                              key={`${currentQuestion?.id || 'default'}-new-passage`}
                              theme="snow"
                              value={newPassageContent}
                              onChange={(value) => dispatch({ type: 'FIELD_UPDATE', payload: { field: 'newPassageContent', value } })}
                              modules={passageQuillModules}
                              style={EDITOR_STYLE.passage}
                              className="font-times max-w-full flex-1"
                              ref={passageQuillRef}
                              placeholder="Enter passage content here..."
                            />
                          </>
                        ) : (
                          // No shared passage linked
                          <div className="flex-1 flex flex-col items-center justify-center p-8">
                            <Text className="text-gray-500 text-center mb-4">
                              No shared passage selected
                            </Text>
                            <div className="grid grid-cols-1 gap-2 md:flex md:gap-3">
                              {currentQuestionIndex > 0 &&
                                examPaper?.sections?.[currentModuleIndex]?.questions?.[currentQuestionIndex - 1]
                                  ?.passage_id && (
                                  <Button
                                    color="white"
                                    onClick={handleLinkToPreviousPassage}
                                    className="w-full md:w-auto"
                                  >
                                    Link to Previous Passage
                                  </Button>
                                )}
                              <Button
                                onClick={handleStartCreatingNewPassage}
                                className="w-full md:w-auto"
                              >
                                Create New Passage
                              </Button>
                              {currentQuestionIndex <
                                currentModuleQuestionIds.length - 1 &&
                                examPaper?.sections?.[currentModuleIndex]?.questions?.[currentQuestionIndex + 1]
                                  ?.passage_id && (
                                  <Button
                                    color="white"
                                    onClick={handleLinkToNextPassage}
                                    className="w-full md:w-auto"
                                  >
                                    Link to Next Passage
                                  </Button>
                                )}
                            </div>
                          </div>
                        )
                      ) : (
                        // Individual passage UI (기존 코드)
                        <>
                          <CustomToolbar
                            quillRef={passageQuillRef}
                            editorId="passage"
                            showEmphasis
                          />
                          <ReactQuill
                            key={`${currentQuestion?.id || 'default'}-individual-passage`}
                            theme="snow"
                            value={editedPassage}
                            onChange={(value) => dispatch({ type: 'FIELD_UPDATE', payload: { field: 'editedPassage', value } })}
                            modules={passageQuillModules}
                            style={EDITOR_STYLE.passage}
                            className="font-times max-w-full flex-1"
                            ref={passageQuillRef}
                          />
                        </>
                      )
                    ) : (
                      <div className="flex-1 flex items-center justify-center">
                        <Text className="text-gray-500 text-center">
                          No passage input required for {examPaper.exam_type}{" "}
                          {getPaperTypeLabel(examPaper.subject)}
                        </Text>
                      </div>
                    )}
                  </div>
                </div>

                {/* 우측: 문제 및 선택지/단답형 영역 */}
                <div className="bg-gray-50 p-4 rounded-lg overflow-hidden">
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <Text className="font-medium">
                        Question {currentQuestionIndex + 1}
                      </Text>
                    </div>

                    <div className="quill-container w-full max-w-full flex flex-col overflow-auto min-h-0 flex-1 border border-gray-300 bg-white rounded">
                      <CustomToolbar
                        quillRef={questionQuillRef}
                        editorId="question"
                      />
                      <ReactQuill
                        key={`${currentQuestion?.id || 'default'}-question`}
                        theme="snow"
                        value={editedQuestion}
                        onChange={(value) => dispatch({ type: 'FIELD_UPDATE', payload: { field: 'editedQuestion', value } })}
                        modules={questionQuillModules}
                        style={EDITOR_STYLE.question}
                        className="font-times max-w-full"
                        ref={questionQuillRef}
                      />
                    </div>
                  </div>

                  {/* 정답 타입 선택 */}
                  <div className="mb-4">
                    <Text className="mb-2 font-medium">Answer Type:</Text>
                    <Select
                      ref={answerTypeSelectRef}
                      value={answerType}
                      onChange={(e) => dispatch({ type: 'SET_ANSWER_TYPE', payload: { answerType: e.target.value, choiceCount } })}
                      onKeyDown={(event) =>
                        handleTabNavigation(
                          event,
                          focusFirstAnswerField,
                          focusQuestionEditor
                        )
                      }
                      className="w-52"
                    >
                      <option value="multiple_choice">Multiple Choice</option>
                      <option value="short_answer">Short Answer</option>
                    </Select>
                  </div>

                  {/* 객관식/단답형 분기 */}
                  {answerType === "multiple_choice" ? (
                    <div className="mb-4">
                      <div className="flex justify-between items-center mb-2">
                        <Text className="font-medium">Choices</Text>
                      </div>
                      {currentChoices.map((choice, index) => (
                        <div
                          key={choice.id}
                          className="flex items-start mb-3 gap-3"
                        >
                          <div className="mt-2">
                            <label
                              htmlFor={`choice_${choice.id}`}
                              className="inline-flex items-center cursor-pointer"
                            >
                              <input
                                type="radio"
                                id={`choice_${choice.id}`}
                                name="correctChoice"
                                checked={correctChoiceId === choice.id}
                                onChange={() => dispatch({ type: 'FIELD_UPDATE', payload: { field: 'correctChoiceId', value: choice.id } })}
                                className="sr-only"
                              />
                              <span
                                className={`w-6 h-6 border border-gray-300 rounded-full flex items-center justify-center ${
                                  correctChoiceId === choice.id
                                    ? "bg-green-500 border-green-500"
                                    : "bg-white"
                                }`}
                              >
                                {correctChoiceId === choice.id && (
                                  <span className="w-3 h-3 bg-white rounded-full"></span>
                                )}
                              </span>
                            </label>
                          </div>
                          <div className="flex-1 w-full max-w-full flex flex-col overflow-auto min-h-0 relative border border-gray-300 bg-white">
                            <div
                              className={`quill-container w-full max-w-full overflow-hidden relative ${
                                correctChoiceId === choice.id
                                  ? "border-2 border-green-300 rounded box-border"
                                  : ""
                              }`}
                            >
                              {correctChoiceId === choice.id && (
                                <div className="absolute top-0 left-0 bg-green-100 text-green-700 px-1 py-0 text-[9px] font-bold rounded-br z-10 border border-green-300">
                                  Correct
                                </div>
                              )}
                              <ReactQuill
                                key={`${currentQuestion?.id || 'default'}-choice-${choice.id}`}
                                theme="snow"
                                value={choice.text}
                                onChange={(value) =>
                                  handleChoiceTextChange(index, value)
                                }
                                modules={
                                  choiceQuillModules[index] ||
                                  NO_TOOLBAR_QUILL_MODULES
                                }
                                style={EDITOR_STYLE.choice}
                                className="font-times max-w-full"
                                ref={(el) => {
                                  choiceQuillRefs.current[index] = el;
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mb-4">
                      <Text className="font-medium mb-2">Short Answer</Text>
                      <Input.TextArea
                        ref={shortAnswerRef}
                        value={shortAnswer}
                        onChange={(e) => dispatch({ type: 'FIELD_UPDATE', payload: { field: 'shortAnswer', value: e.target.value } })}
                        onKeyDown={(event) =>
                          handleTabNavigation(
                            event,
                            focusCategorySelect,
                            focusAnswerTypeSelect
                          )
                        }
                        rows={3}
                        placeholder="Enter the correct answer here..."
                      />
                      <div className="mt-6 text-xl font-bold">
                        Answer Preview:{" "}
                        <span
                          dangerouslySetInnerHTML={{
                            __html: convertToFraction(shortAnswer),
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* 문제 카테고리 선택 */}
                  <div className="mb-4">
                    <Text className="mb-2 font-medium">Category:</Text>
                    <Select
                      ref={categorySelectRef}
                      value={selectedCategoryId}
                      onChange={(e) => dispatch({ type: 'FIELD_UPDATE', payload: { field: 'selectedCategoryId', value: e.target.value } })}
                      onKeyDown={(event) =>
                        handleTabNavigation(
                          event,
                          focusFirstTabField,
                          focusLastAnswerField
                        )
                      }
                      className="w-full"
                    >
                      <option value="">Select category</option>
                      {categoriesData
                        ?.sort((a, b) => a.no - b.no)
                        ?.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.no && category.no > 0 ? `${category.no}. ${category.name}` : category.name}
                          </option>
                        ))}
                    </Select>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-gray-50 p-8 rounded-lg text-center">
              <Text className="text-gray-500">
                No questions in this section. Click + to add one.
              </Text>
            </div>
          )}
        </div>
      </div>
    </PageContainer>
  );
};

export default ExamPaperEditQuestionsPage;
